import { AGENT_URL } from '@/config/env'
import { NOTIFICATIONS_PAGE_SIZE } from '@/config/constants'
import {
  fetchJsonWithAuth,
  mutateJsonWithAuth,
  parsePagination,
} from '@/lib/api'
import type {
  AppNotification,
  NotificationListParams,
  NotificationListResponse,
  NotificationListStatus,
  NotificationTransition,
  UnreadCountResponse,
} from '@/features/notifications/types'

export async function fetchNotifications({
  token,
  status,
  cursor,
  filters,
  limit = NOTIFICATIONS_PAGE_SIZE,
  signal,
}: {
  token: string
  status: NotificationListStatus
  cursor?: string | null
  filters?: NotificationListParams
  limit?: number
  signal?: AbortSignal
}): Promise<NotificationListResponse> {
  const params = new URLSearchParams({
    status,
    limit: String(limit),
  })
  // The relay emits the offset as "Z", never "+00:00", so the cursor survives a
  // query string without encoding. Encode anyway — it is opaque to us.
  if (cursor) params.set('cursor', cursor)
  // URLSearchParams percent-encodes, which the date bounds need: an ISO string
  // carrying a "+00:00" offset would otherwise reach the relay with the offset
  // decoded to a space. Empty values are dropped so a cleared filter is absent
  // rather than sent as "" — the relay treats those differently.
  for (const [name, value] of Object.entries(filters ?? {})) {
    if (value) params.set(name, value)
  }

  const payload = await fetchJsonWithAuth<NotificationListResponse>({
    token,
    url: `${AGENT_URL}/notifications?${params.toString()}`,
    signal,
    errorMessage: 'Failed to load notifications',
  })

  // `relay_json` yields `{}` for an empty upstream body and the proxy forwards
  // that with a 200, so neither field is guaranteed. The inbox indexes into both.
  return {
    ...payload,
    data: payload.data ?? [],
    pagination: parsePagination(payload.pagination, limit),
  }
}

export async function fetchUnreadCount({
  token,
  signal,
}: {
  token: string
  signal?: AbortSignal
}): Promise<UnreadCountResponse> {
  return fetchJsonWithAuth<UnreadCountResponse>({
    token,
    url: `${AGENT_URL}/notifications/unread-count`,
    signal,
    errorMessage: 'Failed to load unread count',
  })
}

export async function transitionNotification({
  token,
  id,
  action,
}: {
  token: string
  id: string
  action: NotificationTransition
}): Promise<{ success: boolean; data: AppNotification }> {
  return mutateJsonWithAuth({
    token,
    url: `${AGENT_URL}/notifications/${id}/${action}`,
    method: 'POST',
    errorMessage: `Failed to ${action} notification`,
  })
}

export async function markAllNotificationsRead({
  token,
}: {
  token: string
}): Promise<{ success: boolean; updated: number }> {
  return mutateJsonWithAuth({
    token,
    url: `${AGENT_URL}/notifications/read-all`,
    method: 'POST',
    errorMessage: 'Failed to mark all read',
  })
}
